import React, { useEffect, useMemo, useState } from 'react';
import { CalendarDays, X } from 'lucide-react';
import { API_ENDPOINTS, apiCall } from '../../config/api';
import { normalizeShipments } from '../../utils/fobAggregation';
import FobCalendar from './FobCalendar';

// Loads the user's complete air / sea export list (the dashboard tables are
// paginated, so they can't be reused here) and shows it in the FOB calendar.
const FobCalendarModal = ({ userId, transportType = 'air', onClose }) => {
  const [rawShipments, setRawShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const url = transportType === 'sea'
          ? `/sea-export/user/${userId}`
          : `${API_ENDPOINTS.GET_USER_SHIPMENTS}?user_id=${userId}`;
        const response = await apiCall(url, { method: 'GET' });
        const data = await response.json();
        if (cancelled) return;
        if (response.ok && data.success && Array.isArray(data.data)) {
          const list = transportType === 'sea'
            ? data.data.filter((s) => s.userId === undefined || String(s.userId) === String(userId))
            : data.data;
          setRawShipments(list);
        } else {
          setRawShipments([]);
          setError('Could not load shipments for the calendar.');
        }
      } catch (err) {
        if (cancelled) return;
        console.error('FOB calendar fetch error:', err);
        setRawShipments([]);
        setError('Could not load shipments for the calendar.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [userId, transportType]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const items = useMemo(() => normalizeShipments(rawShipments, transportType), [rawShipments, transportType]);
  const undatedCount = useMemo(() => items.filter((i) => !i.date).length, [items]);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-2 sm:p-4" onClick={onClose}>
      <div
        className="w-full max-w-[1240px] max-h-[94vh] flex flex-col overflow-hidden rounded-3xl shadow-2xl p-4 sm:p-5
                   bg-white dark:bg-[#0f1728] border border-slate-200 dark:border-slate-700/60"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="FOB calendar"
      >
        <div className="flex items-start justify-between gap-3 mb-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-2xl grid place-items-center flex-shrink-0
                             bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300">
              <CalendarDays size={21} />
            </span>
            <div>
              <h2 className="text-[19px] font-extrabold text-slate-900 dark:text-white leading-tight">
                FOB Calendar - {transportType === 'sea' ? 'Sea Export' : 'Air Export'}
              </h2>
              <p className="text-[13px] text-slate-500 dark:text-slate-400">
                View daily FOB value and shipment details for your exports
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100
                       dark:hover:text-white dark:hover:bg-slate-800 transition"
            aria-label="Close calendar"
          >
            <X size={20} />
          </button>
        </div>

        {error && (
          <p className="mb-4 text-sm text-red-600 dark:text-red-400">{error}</p>
        )}

        <div className="flex-1 min-h-0 flex flex-col">
          <FobCalendar items={items} loading={loading} transportType={transportType} />
        </div>

        {!loading && undatedCount > 0 && (
          <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
            {undatedCount} shipment{undatedCount === 1 ? '' : 's'} without a valid S/B date {undatedCount === 1 ? 'is' : 'are'} not shown.
          </p>
        )}
      </div>
    </div>
  );
};

export default FobCalendarModal;
